import React, { useEffect, useState } from 'react';
import { Upload } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { InboxOutlined } from '@ant-design/icons';
import WarningIcon from '@mui/icons-material/Warning';

import { loadDocumentTypeIdsAndNamesForContract } from '../../store/directusService';
import {
    addParticipantDocuments,
    clearValidationErrors,
    onClearDocumentErrors
} from '../../store/documentService';
import { DOCUMENT_TYPES_ERROR } from '../../utils/formValidation/loadingErrorMessageConstants';
import Button from '../formElements/Button';
import SingleSelect from '../formElements/SingleSelect';
import LoadingSpinner from '../ui/LoadingSpinner';
import ScreenError from '../ui/messageBanners/ScreenError';

import '../../commonStyles/antUploaderStyles.css';
import app from '../../app.module.css';
import form from '../../commonStyles/formStyles.module.css';
import uploader from '../../commonStyles/uploader.module.css';
import local from './documents.module.css';

const { Dragger } = Upload;
export const MAX_FILE_SIZE = 20;
export const FILE_TYPES = '.doc, .docx, .pdf, .csv, .rtf, .jpg, .png, .odt, .ods';
const acceptedFileTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf',
    'text/csv',
    'application/rtf',
    'image/jpeg',
    'image/png',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet'
];
export const FILE_SIZE_ERROR_MESSAGE = `File size must be smaller than ${MAX_FILE_SIZE}MB`;

const AddDocument = ({
    onClose,
    onCancel,
    isDisabled,
    btnText = 'Close',
    currentDocumentType,
    hideCancel = false
}) => {
    const dispatch = useDispatch();

    //LOCAL STATE
    const [documentType, setDocumentType] = useState({});
    const [file, setFile] = useState({});

    const [wrongFileType, setWrongFileType] = useState({});
    const [postUploadWarnings, setPostUploadWarnings] = useState([]);
    const [preUploadErrors, setPreUploadErrors] = useState({});
    const [postUploadErrors, setPostUploadErrors] = useState({});

    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const documentTypes = useSelector(
        (state) => state.entities.directusService.documentTypeIdsAndNamesForContract
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantDocumentErrors = useSelector(
        (state) => state.entities.documentService.participantDocumentErrors
    );
    const validationErrors = useSelector(
        (state) => state.entities.documentService.validationErrors
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // VENDOR FN
    const uploadProps = {
        name: 'file',
        onRemove() {
            setFile({});
            setPreUploadErrors({});
            setPostUploadErrors({});
            setPostUploadWarnings([]);
            setWrongFileType({});
        },
        onDrop() {
            // this is still needed when multiple: false - but req goes to customReq. - see DAR uploader for when multiple: true
        },
        multiple: false,
        disabled: typeof documentType !== 'object' || Object.keys(documentType).length < 1,
        customRequest(info) {
            if (!acceptedFileTypes.includes(info.file.type)) {
                setWrongFileType(info.file);
            } else {
                const isValidSize = (size) => size / 1024 / 1024 <= MAX_FILE_SIZE;
                !isValidSize(info.file.size) &&
                    configFileError('fileSize', FILE_SIZE_ERROR_MESSAGE);
                setFile(info.file);
            }
        }
    };

    // USEEFFECTS
    useEffect(() => {
        currentParticipant?.contractId &&
            dispatch(loadDocumentTypeIdsAndNamesForContract(currentParticipant.contractId));
    }, []);

    useEffect(() => {
        if (!documentTypes?.length) setLoadingContentMessage(DOCUMENT_TYPES_ERROR);
        if (currentDocumentType) {
            const documentType = documentTypes.find(
                (el) => el.name.toLowerCase() === currentDocumentType.name.toLowerCase()
            );
            documentType && setDocumentType(documentType);
        }
    }, [currentDocumentType, documentTypes]);

    useEffect(() => {
        if (Object.keys(file).length > 0 && Object.keys(documentType)?.length > 1) onUpload();
        else if (validationErrors && Object.keys(validationErrors).length > 0) {
            const errors = Object.entries(validationErrors).reduce(
                (acc, [key, val]) => ({
                    ...acc,
                    [key]: { ...val, message: `${key}: ${val.message}` }
                }),
                {}
            );
            setPostUploadErrors(errors);
            dispatch(clearValidationErrors());
        } else if (participantDocumentErrors?.length > 0) {
            setPostUploadWarnings(participantDocumentErrors);
            dispatch(onClearDocumentErrors());
        } else if (
            !participantDocumentErrors?.length &&
            !postUploadWarnings?.length &&
            successMessage !== '' &&
            successMessage === successMsg
        ) {
            clearUploader();
            onClose();
        }
    }, [documentType, file, successMessage, participantDocumentErrors, validationErrors]);

    // FNS
    const configFileError = (key, message) => {
        setPreUploadErrors((prev) => ({ ...prev, [key]: { error: true, message } }));
    };

    const clearError = (key) => {
        if (key in preUploadErrors && preUploadErrors[key].error)
            setPreUploadErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
        else if (key in postUploadErrors && postUploadErrors[key].error)
            setPostUploadErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const clearUploader = () => {
        setPreUploadErrors({});
        setPostUploadErrors({});
        setPostUploadWarnings([]);
        setWrongFileType({});
        setFile({});
        setSuccessMsg('');
        setDocumentType({});
    };

    // EVENT HANDLERS
    const onUpload = () => {
        if (Object.keys(file).length < 1) {
            onClose();
            return;
        }

        if (Object.values(preUploadErrors).some((el) => el.error)) return;
        const payload = new FormData();
        payload.append('file', file);
        payload.append('userId', loggedInUser.id);
        payload.append('typeId', documentType?.id);
        setFile({});
        const msg = `Document: ${file.name} has been uploaded`;
        setSuccessMsg(msg);
        dispatch(addParticipantDocuments(currentParticipant.id, payload, msg));
    };

    const onTypeChange = (id) => {
        const type = id ? documentTypes.find((el) => el.id === id) || {} : {};
        setDocumentType(type);
    };

    const onAdded = () => {
        clearUploader();
        onClose();
    };

    const onExit = () => {
        clearUploader();
        onCancel();
    };

    // RENDER
    return !documentTypes?.length ? (
        <LoadingSpinner content={loadingContentMessage} />
    ) : (
        <div className={`${app.fadein} ${local.documentForm}`} data-testid="add_document_form">
            <div className={local.optionInput}>
                <SingleSelect
                    id="typeId"
                    label="Document Type"
                    placeholder="Select document type..."
                    disabled={isDisabled}
                    mandatory={true}
                    menuItems={documentTypes || []}
                    selectedId={documentType?.id || ''}
                    selected={documentType || currentDocumentType || {}}
                    onChange={(id) => onTypeChange(id)}
                />
            </div>

            <label htmlFor="uploader" className={form.formLabel}>
                Attach Document
            </label>
            <div>
                <Dragger
                    accept={FILE_TYPES}
                    onDrop={(e) => uploadProps.onDrop(e)}
                    data-testid={`dragger`}
                    {...uploadProps}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    <p className="ant-upload-hint">{`Support for fileTypes: ${FILE_TYPES}`}</p>
                </Dragger>
            </div>

            {Object.keys(wrongFileType).length > 0 && (
                <div className={uploader.uploaderFileWarning} data-testid="wrong_file_type_warning">
                    <div className={uploader.warningText}>
                        <WarningIcon />
                        This file could not be added as it is the wrong type:
                        {wrongFileType.name}
                    </div>
                </div>
            )}

            {postUploadWarnings.length > 0 &&
                (postUploadWarnings.length === 1 ? (
                    <div className={uploader.uploaderFileWarning}>
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            This file was not added: {postUploadWarnings[0].filename} - Reason:{' '}
                            {postUploadWarnings[0].errorCode.replaceAll('_', ' ')}
                        </div>
                    </div>
                ) : (
                    <div className={uploader.uploaderFileWarning}>
                        <div className={uploader.warningText}>
                            <WarningIcon />
                            These files were not added:
                        </div>
                        {postUploadWarnings.map((el) => (
                            <div key={el.filename} className={uploader.warningRow}>
                                {el.filename} - Reason: {el.errorCode.replaceAll('_', ' ')}
                            </div>
                        ))}
                    </div>
                ))}

            {Object.values({ ...preUploadErrors, ...postUploadErrors }).some((el) => el.error) && (
                <div className={local.uploadError}>
                    {Object.entries({ ...preUploadErrors, ...postUploadErrors }).map((el) =>
                        el[1].error ? (
                            <div key={el[0]}>
                                <ScreenError
                                    error={el[1]}
                                    name={el[0]}
                                    clearError={clearError}
                                    customClass="relativePos"
                                    allowDismiss={false}
                                />
                            </div>
                        ) : null
                    )}
                </div>
            )}

            <div className={local.formActions}>
                <Button
                    id="documentUploaderCloseButton"
                    content={btnText}
                    onClick={onAdded}
                    disabled={isDisabled}
                />
                {!hideCancel && (
                    <div
                        className={local.cancelLink}
                        onClick={onExit}
                        data-testid="add_document_cancel_button">
                        Cancel X
                    </div>
                )}
            </div>
        </div>
    );
};

AddDocument.propTypes = {
    onClose: PropTypes.func,
    onCancel: PropTypes.func,
    isDisabled: PropTypes.bool,
    btnText: PropTypes.string,
    currentDocumentType: PropTypes.object,
    hideCancel: PropTypes.bool
};

export default AddDocument;
